import React from 'react';
import differenceInDays from 'date-fns/difference_in_days';

import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';
import { useTranslation } from '@crehana/i18n';
import { LANGUAGES } from '@crehana/ts-types';
import { PrimaryButton } from '@crehana/ui';

import { useAmplitude } from '@crehana/analytics';

import { CATALOG_AMPLITUDE_PAGE_NAME } from 'Jsx/Catalog/constants';

import type { CourseProps } from '../types';

import CustomWrapper from '../utils/CustomWrapper';
import getEnrollmentUrlWithSource from '../utils/getEnrollmentUrlWithSource';
import { handleEnrollInPreviewTrial } from '../utils';

import {
  Wrapper,
  Image,
  Tag,
  CardBody,
  Title,
  Enrollments,
  Professor,
  Prices,
  BrasilianTag,
  AddToCart,
  AddToCartMobile,
} from '../components';

import CourseCardV3Wrapper from './CourseCardV3Wrapper';

const CourseCardV3Full: React.FC<
  CourseProps & { isAvailableForPreviewTrial: boolean }
> = ({
  title,
  url,
  metaDescription,
  affiliateCommission,
  affiliateUrl,
  isDark,
  duration,
  enrollments,
  imagePlaceholder,
  isEnrollment,
  level,
  hidePreview,
  prices,
  promoImage,
  promoImageAnimated,
  // @ts-expect-error
  styles,
  urlToBuyNow,
  originalId,
  professor,
  preSale,
  trailerSource,
  courseType,
  clickTitleHandler,
  lazyImageProps,
  categoryObj,
  principalSoftware,
  principalSoftwareObj,
  courseTag,
  imgxParams,
  shoppingCartAdded,
  urlToAddToCart,
  score,
  description,
  enrollmentUrl,
  enrollmentAccessPreviewUrl,
  audio,
  subtitle,
  hidePreviewCTA,
  hideEnrollmentSection,
  isInSlider,
  courseCardPage,
  launchDate,
  // We must avoid using custom addToCart and removeFromCart functions.
  // Especial cases are for shopping cart and shopping cart pop up
  customInShoppingCart,
  customAddToCart,
  customRemoveFromCart,
  pageType,
  onClickBuyNow,
  onClickEnrollNow,
  showProfessor = true,
  classification,
  languageTag,
  lazyImage = true,
  priority = false,
  showButtons = true,
  showPrice = true,
  product,
  beforeBuy,
  beforePreview,
  customOnClickImage,
  pageId,
  temporalAccessDurationLabel,
  // @ts-expect-error
  isBlackTheme,
  renderCustomCallbackButtons,
  source,
  sourceDetail,
  priceVariant,
  isSavedCourse,
  isOrganization,
  isAvailableForPreviewTrial = false,
}) => {
  const { logEvent } = useAmplitude();
  const { t, languageCode } = useTranslation();
  const showBrasilianTag = languageCode === LANGUAGES.PT && languageTag;
  const { me: user } = useMe();

  const userIsPremium = user && user.isPremiumMember;
  const userIsB2B = user?.organization;
  const numberOfDaysPublished =
    launchDate && differenceInDays(new Date(), launchDate);
  const isUserSofeMembership = user && user?.isSofeMembership;
  const courseIsOneMonthOld =
    numberOfDaysPublished && numberOfDaysPublished <= 30;
  const isUserSofeAndCourseIsOneMonthOld =
    isUserSofeMembership && courseIsOneMonthOld;
  const courseUrl = url;
  const _urlToBuyNow = urlToBuyNow;

  return (
    <CourseCardV3Wrapper
      audio={audio}
      subtitle={subtitle}
      title={title}
      metaDescription={metaDescription}
      url={courseUrl}
      affiliateCommission={affiliateCommission}
      affiliateUrl={affiliateUrl}
      isDark={isDark}
      duration={duration}
      enrollments={enrollments}
      imagePlaceholder={imagePlaceholder}
      isEnrollment={isEnrollment}
      level={level}
      professor={professor}
      preSale={preSale}
      trailerSource={trailerSource}
      hidePreview={hidePreview}
      prices={prices}
      promoImage={promoImage}
      promoImageAnimated={promoImageAnimated}
      styles={styles}
      urlToBuyNow={_urlToBuyNow}
      user={user}
      originalId={originalId}
      courseType={courseType}
      principalSoftware={principalSoftware}
      principalSoftwareObj={principalSoftwareObj}
      courseTag={courseTag}
      imgxParams={imgxParams}
      clickTitleHandler={clickTitleHandler}
      lazyImageProps={lazyImageProps}
      lazyImage={lazyImage}
      priority={priority}
      categoryObj={categoryObj}
      enrollmentUrl={getEnrollmentUrlWithSource({
        originalId,
        enrollmentUrl,
        source,
        sourceDetail,
      })}
      enrollmentAccessPreviewUrl={getEnrollmentUrlWithSource({
        originalId,
        enrollmentUrl: enrollmentAccessPreviewUrl,
        source,
        sourceDetail,
      })}
      source={source}
      sourceDetail={sourceDetail}
      score={score}
      description={description}
      urlToAddToCart={urlToAddToCart}
      shoppingCartAdded={shoppingCartAdded}
      customAddToCart={customAddToCart}
      customInShoppingCart={customInShoppingCart}
      customRemoveFromCart={customRemoveFromCart}
      hidePreviewCTA={hidePreviewCTA}
      isInSlider={isInSlider}
      courseCardPage={courseCardPage}
      pageType={pageType}
      pageId={pageId}
      onClickBuyNow={onClickBuyNow}
      onClickEnrollNow={onClickEnrollNow}
      showProfessor={showProfessor}
      classification={classification}
      languageTag={languageTag}
      product={product}
      beforeBuy={beforeBuy}
      beforePreview={beforePreview}
      customOnClickImage={customOnClickImage}
      temporalAccessDurationLabel={temporalAccessDurationLabel}
      isBlackTheme={isBlackTheme}
      userIsPremium={userIsPremium}
      userIsB2B={userIsB2B}
      renderCustomCallbackButtons={renderCustomCallbackButtons}
      // @ts-expect-error
      isUserSofeAndCourseIsOneMonthOld={isUserSofeAndCourseIsOneMonthOld}
      priceVariant={priceVariant}
      isOrganization={isOrganization}
      isAvailableForPreviewTrial={isAvailableForPreviewTrial}
    >
      <Wrapper>
        <Tag />
        <Image>
          {showBrasilianTag && <BrasilianTag />}
          {!hideEnrollmentSection && <Enrollments />}
        </Image>

        <CardBody>
          <Title isSavedCourse={isSavedCourse} />
          {showProfessor && <Professor />}
          {showPrice && !isOrganization && !isAvailableForPreviewTrial && (
            <Prices />
          )}
          {showButtons && !user?.hasOrganization && (
            <CustomWrapper>
              {isAvailableForPreviewTrial ? (
                <PrimaryButton
                  full
                  className="mt-16"
                  label={t('PREVIEW_TRIAL_START_BUTTON')}
                  onClick={() => {
                    if (enrollmentUrl) {
                      logEvent({
                        event: 'Click Preview Trial CourseCard',
                        data: {
                          source: CATALOG_AMPLITUDE_PAGE_NAME,
                          course: title,
                        },
                      });
                      handleEnrollInPreviewTrial(Boolean(user), enrollmentUrl);
                    } else {
                      throw new Error(
                        `Course ID: ${originalId}: ${title} doesn't have enrollmentUrl`,
                      );
                    }
                  }}
                />
              ) : (
                <CustomWrapper>
                  <CustomWrapper className="hidden sm:block">
                    <AddToCart />
                  </CustomWrapper>
                  <CustomWrapper className="block sm:hidden">
                    <AddToCartMobile />
                  </CustomWrapper>
                </CustomWrapper>
              )}
            </CustomWrapper>
          )}
        </CardBody>
      </Wrapper>
    </CourseCardV3Wrapper>
  );
};

export default CourseCardV3Full;
